/**
 * OpenQuestion — pergunta aberta com texto livre.
 *
 * Slide do tipo mission com missionId='lab.form.openQuestion'. Apropriado pra
 * concepções prévias/finais (pré/pós) via campo pairId. Se showPrevAnswer e o
 * adapter já tem resposta prévia pareada, mostra lado a lado depois do envio.
 *
 * Schema do frontmatter — ver SPEC-ATLAS-V2 §2.2.
 */

import { useState } from 'react'

import { useAdapter } from '../../adapter'
import type { MissionComponentProps } from '../../games/atlas/components'
import { avatarColor, initials } from './responseCardUtils'
import { useRevealedResponses } from './useRevealedResponses'

export function OpenQuestion(props: MissionComponentProps) {
  const adapter = useAdapter()
  const config = props.slideConfig ?? {}
  const slideIndex = props.slideIndex ?? 0
  const prompt = (config.prompt as string) || ''
  const placeholder = (config.placeholder as string) || ''
  const maxLength = (config.maxLength as number) || 1000
  const pairId = (config.pairId as string) || ''
  const showPrev = Boolean(config.showPrevAnswer)

  // "Você respondeu antes" (pós com showPrevAnswer): a resposta ANTERIOR pareada
  // por pairId — o backend manda sempre a de menor slideIndex (o pré).
  const prev = pairId ? adapter.getPairedResponse(pairId) : null
  const prevValue =
    showPrev && prev && prev.kind === 'openQuestion' && prev.slideIndex !== slideIndex
      ? String(prev.value)
      : null

  // Pre-popula com a própria resposta DESTE slide (sobrevive a voltar o slide).
  const own = adapter.getOwnResponse(slideIndex)
  const initial = own && own.kind === 'openQuestion' ? String(own.value) : ''
  const [value, setValue] = useState(initial)
  const [submitted, setSubmitted] = useState(initial.length > 0)

  // Mural: respostas que o professor liberou pra todos verem.
  const revealed = useRevealedResponses(slideIndex)

  function handleSubmit() {
    if (!pairId || !value.trim()) return
    adapter.submitOpenQuestion({ pairId, slideIndex, value })
    setSubmitted(true)
    if (props.onMissionEvent) {
      props.onMissionEvent('submitted', { pairId, valueLength: value.length })
    }
  }

  return (
    <div
      style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: 'var(--spacing-lab-5, 24px)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-lab-4, 16px)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-lab-xl, 1.5rem)' }}>{prompt}</h2>

      {prevValue && (
        <aside
          style={{
            padding: 12,
            background: 'var(--color-lab-bg-2, #F2F2ED)',
            borderLeft: '3px solid var(--color-lab-accent, #6aa)',
            fontSize: '0.9em',
          }}
        >
          <div style={{ opacity: 0.7, marginBottom: 4 }}>Você respondeu antes:</div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{prevValue}</div>
        </aside>
      )}

      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value.slice(0, maxLength))}
        placeholder={placeholder}
        disabled={props.readOnly}
        rows={8}
        style={{
          width: '100%',
          padding: 12,
          fontSize: 'var(--text-lab-base, 1rem)',
          fontFamily: 'inherit',
          border: '1px solid var(--color-lab-rule, #D8D5CB)',
          borderRadius: 6,
          resize: 'vertical',
        }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ opacity: 0.6, fontSize: '0.85em' }}>
          {value.length} / {maxLength}
        </span>
        <button
          onClick={handleSubmit}
          disabled={props.readOnly || !value.trim() || !pairId}
          style={{
            padding: '8px 24px',
            background: submitted
              ? 'var(--color-lab-accent, #6aa)'
              : 'var(--color-lab-accent, #5B2DB8)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 'var(--text-lab-base, 1rem)',
          }}
        >
          {submitted ? 'Atualizar' : 'Enviar'}
        </button>
      </div>

      {submitted && (
        <div style={{ color: 'var(--color-lab-mint, #1E9E6A)', fontSize: '0.9em' }}>
          ✓ Resposta enviada. Você pode editar e reenviar — o professor vê o que você mandou.
        </div>
      )}

      {revealed.length > 0 && (
        <section style={{ marginTop: 'var(--spacing-lab-5, 24px)' }}>
          <h3
            style={{
              margin: '0 0 var(--spacing-lab-3, 12px)',
              fontSize: 'var(--text-lab-lg, 1.125rem)',
              fontWeight: 600,
              color: 'var(--color-lab-ink, #0F1115)',
              display: 'flex',
              alignItems: 'baseline',
              gap: 8,
            }}
          >
            Em destaque pelo professor
            <span style={{ color: 'var(--color-lab-ink-3, #555B66)', fontWeight: 400, fontSize: 'var(--text-lab-sm, 0.9rem)' }}>
              {revealed.length}
            </span>
          </h3>
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 'var(--spacing-lab-3, 12px)',
            }}
          >
            {revealed.map((r) => {
              const mine = r.membershipId === props.membershipId
              return (
                <li
                  key={r.membershipId}
                  style={{
                    background: 'var(--color-lab-bg, #fff)',
                    border: mine
                      ? '1px solid var(--color-lab-accent, #5B2DB8)'
                      : '1px solid var(--color-lab-rule, #D8D5CB)',
                    borderRadius: 'var(--radius-lab-lg, 16px)',
                    padding: 'var(--spacing-lab-4, 16px)',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacing-lab-3, 12px)',
                  }}
                >
                  <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span
                      aria-hidden
                      style={{
                        width: 34,
                        height: 34,
                        flexShrink: 0,
                        borderRadius: '50%',
                        background: avatarColor(r.membershipId),
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {initials(r.displayName)}
                    </span>
                    <span
                      style={{
                        fontWeight: 600,
                        color: 'var(--color-lab-ink, #0F1115)',
                        fontSize: 'var(--text-lab-sm, 0.9rem)',
                      }}
                    >
                      {r.displayName}
                    </span>
                    {mine && (
                      <span
                        style={{
                          marginLeft: 'auto',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'var(--color-lab-accent, #5B2DB8)',
                          background: 'var(--color-lab-bg-2, #F2F2ED)',
                          padding: '2px 8px',
                          borderRadius: 999,
                        }}
                      >
                        você
                      </span>
                    )}
                  </header>
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--color-lab-ink-2, #2A2D33)',
                      fontSize: 'var(--text-lab-base, 1rem)',
                      lineHeight: 1.55,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {r.value}
                  </p>
                </li>
              )
            })}
          </ul>
        </section>
      )}
    </div>
  )
}
